import {
  AutoComplete,
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  Divider,
  Icons,
  Input,
  Popconfirm,
  Row,
  Select,
  Skeleton,
  Table,
  Typography,
  notification,
} from '@pankod/refine-antd';
import { EPaymentChannel, EPaymentType, ESalesChannel, EStatus } from 'enums';
import { PAYMENT_PROVIDERS } from 'constants/paymentProviders';
import Handlebars from 'handlebars';
import { useDebounce, useUserData } from 'hooks';
import { IClient, IPayment } from 'interfaces';
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { getClient } from 'services/client';
import { getAllCompanyConfig, getCompanyInfo } from 'services/config';
import { ShowroomStock } from 'components/pos/ShowroomStock';
import { getPOSVariantInventoriesByWarehouse } from 'services/inventory';
import { SearchIndex, getSearchData } from 'services/meilisearch';
import { filterPosSearchOptions } from 'utilities/pos-product-search';
import { showsVat } from 'utilities/sale-vat';
import { CreateSaleData, createSale } from 'services/sale';
import { getTransactionToken } from 'services/transaction';
import { IWarehouseDropDown, getWarehousesDropdown } from 'services/warehouse';
import MetaData from 'utilities/metadata';
import {
  DiscountEntry,
  EDiscountEntryType,
  formatInvoiceDiscountEntry,
} from 'utilities/discount-entry';
import { invoiceTemplate, posReceiptTemplate } from 'utilities/invoice';
import { calculateSaleTotals } from 'utilities/sale-totals';
import { useOrganizationVatRate } from 'hooks/useOrganizationVatRate';
import { useFeatureFlag } from 'hooks/useFeatureFlags';
import { InvoiceProfileSelect } from 'components/invoice-profile/InvoiceProfileSelect';
import { getInvoiceHeader } from 'services/invoice-profile';
import { invoiceHeaderToReceiptCompany } from 'utilities/invoice-profile';
import { CashMemoSampleButton } from 'components/cash-memo/CashMemoSampleButton';
import { printPosInvoice } from 'utilities/print';
import {
  formatItemDiscountEntry,
  getItemDiscountAmount,
  getItemDiscountEntry,
  getItemNetTotal,
  describeItemDiscountEquivalent,
  getPosSaleDiscountCost,
} from './pos-discount';
import {
  IS_DISCOUNT_IN_PERCENTAGE,
  formatCurrency,
  getAddressAsString,
  getRandomString,
} from 'utilities/shared';

interface IWarehouse {
  id: string;
  name: string;
}

interface ICartItem {
  id: string;
  name: string;
  sku: string;
  url: string | null;
  unitPrice: number;
  totalUnit: number;
  quantity: number;
  discount: number;
  isDiscountInPercentage: boolean;
}

interface IOption {
  value: string;
  label: string | React.ReactNode;
}

enum ActionKind {
  SetLoading = 'SET_LOADING',
  SetWarehouses = 'SET_WAREHOUSES',
  SetSelectedWarehouse = 'SET_SELECTED_WAREHOUSE',
  SetCartOptions = 'SET_CART_OPTIONS',
  IncreaseCartOptionItemQuantity = 'INCREASE_CART_OPTION_ITEM_QUANTITY',
  DecreaseCartOptionItemQuantity = 'Decrease_CART_OPTION_ITEM_QUANTITY',
  ResetCartOptionItemsQuantity = 'RESET_CART_OPTION_ITEMS_QUANTITY',
  IncreaseCartItemQuantity = 'INCREASE_CART_ITEM_QUANTITY',
  DecreaseCartItemQuantity = 'DECREASE_CART_ITEM_QUANTITY',
  AddItem = 'ADD_ITEM',
  RemoveItem = 'REMOVE_ITEM',
  EmptyCart = 'EMPTY_CART',
  ChangeQuantity = 'CHANGE_QUANTITY',
  ChangeItemDiscount = 'CHANGE_ITEM_DISCOUNT',
}

type Action =
  | { type: ActionKind.SetLoading; payload: boolean }
  | { type: ActionKind.SetWarehouses; payload: IWarehouseDropDown[] }
  | { type: ActionKind.SetSelectedWarehouse; payload: string }
  | { type: ActionKind.SetCartOptions; payload: ICartItem[] }
  | { type: ActionKind.IncreaseCartOptionItemQuantity; payload: string }
  | { type: ActionKind.DecreaseCartOptionItemQuantity; payload: string }
  | { type: ActionKind.ResetCartOptionItemsQuantity }
  | { type: ActionKind.IncreaseCartItemQuantity; payload: string }
  | { type: ActionKind.DecreaseCartItemQuantity; payload: string }
  | { type: ActionKind.AddItem; payload: string }
  | { type: ActionKind.RemoveItem; payload: string }
  | { type: ActionKind.EmptyCart }
  | { type: ActionKind.ChangeQuantity; payload: { id: string; quantity: number } }
  | {
      type: ActionKind.ChangeItemDiscount;
      payload: {
        id: string;
        discount: number;
        isPercentage: boolean;
      };
    };

interface IState {
  isLoading: boolean;
  warehouses: IWarehouse[];
  selectedWarehouse?: string;
  cartOptions: ICartItem[];
  cart: ICartItem[];
}

const initialState: IState = {
  isLoading: false,
  warehouses: [],
  selectedWarehouse: undefined,
  cartOptions: [],
  cart: [],
};

const posReducer = (state: IState, action: Action) => {
  switch (action.type) {
    case ActionKind.SetLoading:
      return { ...state, isLoading: action.payload };
    case ActionKind.SetWarehouses:
      return { ...state, warehouses: action.payload };
    case ActionKind.SetSelectedWarehouse:
      return { ...state, selectedWarehouse: action.payload };
    case ActionKind.SetCartOptions:
      return { ...state, cartOptions: action.payload };
    case ActionKind.IncreaseCartOptionItemQuantity:
      const cartItem = state.cart.find((item) => item.id === action.payload);
      const cartItemQuantity = cartItem?.quantity ?? 0;

      return {
        ...state,
        cartOptions: state.cartOptions.map((item) => {
          if (item.id === action.payload && item.quantity < item.totalUnit - cartItemQuantity) {
            return { ...item, quantity: item.quantity + 1 };
          }
          return item;
        }),
      };
    case ActionKind.DecreaseCartOptionItemQuantity:
      return {
        ...state,
        cartOptions: state.cartOptions.map((option) => {
          if (option.id === action.payload && option.quantity > 1) {
            return { ...option, quantity: option.quantity - 1 };
          }
          return option;
        }),
      };
    case ActionKind.ResetCartOptionItemsQuantity:
      return {
        ...state,
        cartOptions: state.cartOptions.map((item) => ({ ...item, quantity: 1 })),
      };
    case ActionKind.AddItem:
      const option = state.cartOptions.find((item) => item.id === action.payload);
      if (!option) return state;

      const isItemExistInCart = !!state.cart.find((item) => item.id === option.id);
      if (isItemExistInCart) {
        return {
          ...state,
          cart: state.cart.map((item) => {
            if (item.id === option.id) {
              return { ...item, quantity: item.quantity + option.quantity };
            }
            return { ...item };
          }),
        };
      } else {
        return {
          ...state,
          cart: [
            ...state.cart,
            {
              ...option,
              discount: 0,
              isDiscountInPercentage: false,
            },
          ],
        };
      }
    case ActionKind.RemoveItem:
      return { ...state, cart: state.cart.filter((item) => item.id !== action.payload) };
    case ActionKind.EmptyCart:
      return { ...state, cart: [] };
    case ActionKind.IncreaseCartItemQuantity:
      return {
        ...state,
        cart: state.cart.map((item) => {
          if (item.id === action.payload && item.quantity < item.totalUnit) {
            return { ...item, quantity: item.quantity + 1 };
          }
          return item;
        }),
      };
    case ActionKind.DecreaseCartItemQuantity:
      return {
        ...state,
        cart: state.cart.map((item) => {
          if (item.id === action.payload && item.quantity > 1) {
            return { ...item, quantity: item.quantity - 1 };
          }
          return item;
        }),
      };
    case ActionKind.ChangeQuantity:
      return {
        ...state,
        cart: state.cart.map((item) => {
          if (item.id === action.payload.id && action.payload.quantity <= item.totalUnit) {
            return { ...item, quantity: action.payload.quantity };
          }
          return { ...item };
        }),
      };
    case ActionKind.ChangeItemDiscount:
      return {
        ...state,
        cart: state.cart.map((item) => {
          if (item.id === action.payload.id) {
            return {
              ...item,
              discount: action.payload.discount,
              isDiscountInPercentage: action.payload.isPercentage,
            };
          }
          return item;
        }),
      };
    default:
      return state;
  }
};

export const Pos = () => {
  const { vatRatePercent } = useOrganizationVatRate();
  const cashMemoPreviewEnabled = useFeatureFlag('pos.cashMemoPreview');
  const invoiceProfilesEnabled = useFeatureFlag('sale.invoiceProfiles');
  const [invoiceProfileId, setInvoiceProfileId] = useState<string>();
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);
  const [productSearchValue, setProductSearchValue] = useState<string>('');
  const [{ isLoading, warehouses, selectedWarehouse, cartOptions, cart }, dispatch] = useReducer(
    posReducer,
    initialState,
  );
  const [isQuotation, setIsQuotation] = useState<boolean>(false);
  const [customer, setCustomer] = useState<{
    isSearching: boolean;
    isFetching: boolean;
    searchValue: string | null;
    options: IOption[];
    selected: IClient | null;
    newCustomer: { name: string; number: string; countryCode: string } | null;
  }>({
    isSearching: false,
    isFetching: false,
    searchValue: null,
    options: [],
    selected: null,
    newCustomer: null,
  });
  const [discountInput, setDiscountInput] = useState<number>(0);
  const [cashIn, setCashIn] = useState<number>(0);
  const [isPaymentLoading, setIsPaymentLoading] = useState<boolean>(false);
  const [isPaymentReceiptLoading, setIsPaymentReceiptLoading] = useState<boolean>(false);
  const [selectedProvider, setSelectedProvider] = useState<string>('Cash');
  const cartOptionIndexRef = useRef<number>(0);
  const { user } = useUserData();
  const newCustomerNumberRef = useRef<string>();
  const [newCustomerName, setNewCustomerName] = useState<string>('');
  const [newCustomerNumber, setNewCustomerNumber] = useState<string>('');
  const [newCustomerCountryCode, setNewCustomerCountryCode] = useState<string>('+880');
  const [isManuallyAdding, setIsManuallyAdding] = useState<boolean>(false);
  const searchTextRef = useRef<string>('');
  const debouncedCustomerSearchValue = useDebounce(customer.searchValue, 500);
  const [isDiscountInPercentage, setIsDiscountInPercentage] = useState<boolean>(() => {
    const data = window.localStorage.getItem(IS_DISCOUNT_IN_PERCENTAGE);
    if (data && typeof data === 'string') {
      return JSON.parse(data);
    } else {
      return false;
    }
  });
  const [paymentInfo, setPaymentInfo] = useState({
    cardNumber: '',
    approvalCode: '',
    transactionId: '',
    phoneNumber: '',
    bankRef: '',
  });

  const cardTitle = isQuotation ? 'Quotation' : 'Transaction';

  const selectedProviderObj = PAYMENT_PROVIDERS.find((p) => p.name === selectedProvider) || {
    name: 'Cash',
    channel: EPaymentChannel.Cash,
  };
  const derivedPaymentChannel = selectedProviderObj.channel;

  // Per-item discount totals for all items in the cart = sum of getItemDiscountAmount(item) for each item
  const perItemDiscountTotal = cart.reduce((total, item) => {
    return total + getItemDiscountAmount(item);
  }, 0);

  // subtotal = sum of (unitPrice * quantity) for all items (before any discounts)
  const subtotal = cart.reduce((total, item) => {
    return total + item.unitPrice * item.quantity;
  }, 0);

  // subtotalAfterItemDiscounts = subtotal minus all per-item discounts
  const subtotalAfterItemDiscounts = subtotal - perItemDiscountTotal;

  // Order-level (global) discount applied on top of per-item discounts
  const orderDiscountAmount = isDiscountInPercentage
    ? Number((subtotalAfterItemDiscounts * (discountInput / 100)).toFixed(2))
    : Number(discountInput.toFixed(2));

  const orderDiscountEntry: DiscountEntry = discountInput
    ? {
        discountEntryValue: discountInput,
        discountEntryType: isDiscountInPercentage
          ? EDiscountEntryType.Percent
          : EDiscountEntryType.Amount,
      }
    : { discountEntryValue: null, discountEntryType: null };
  const orderDiscountEntryLabel = formatInvoiceDiscountEntry(
    orderDiscountEntry.discountEntryValue,
    orderDiscountEntry.discountEntryType,
    orderDiscountAmount,
  );

  // Total discount shown in summary = per-item + order-level
  const totalDiscountAmount = Number((perItemDiscountTotal + orderDiscountAmount).toFixed(2));

  // The sale total exactly as the API saves it: lines rounded, the organization VAT rate while
  // sale.vatRate is on (else 0), less the order discount. Payment, cash and change use it, so a
  // VAT sale is never saved as partly due.
  const posSaleTotals = calculateSaleTotals({
    saleProducts: cart.map((item) => ({
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      discountCost: getItemDiscountAmount(item),
    })),
    vatPercentage: vatRatePercent,
    shipmentCost: 0,
    invoiceDiscountCost: getPosSaleDiscountCost(orderDiscountAmount),
  });
  const subtotalTaxAmount = posSaleTotals.vatAmount;
  // Net total before the order discount, as the receipt prints it.
  const totalAmount = Number((posSaleTotals.linesSubTotal + subtotalTaxAmount).toFixed(2));
  const totalPayableAmount = posSaleTotals.netTotal;
  const returnAmount = cashIn === 0 ? 0 : cashIn - totalPayableAmount;
  const isCartEmpty = cart.length === 0;
  const filteredCartOptions = filterPosSearchOptions(cartOptions, productSearchValue);
  const enabledCartOptionsLength = filteredCartOptions.filter((item) => item.totalUnit > 0).length;

  const isCardInfoIncomplete =
    derivedPaymentChannel === EPaymentChannel.Card &&
    (!paymentInfo.cardNumber || !paymentInfo.approvalCode);

  const isMfsIncomplete =
    derivedPaymentChannel === EPaymentChannel.MFS && !paymentInfo.transactionId;

  const isBankIncomplete = derivedPaymentChannel === EPaymentChannel.Bank && !paymentInfo.bankRef;

  const isCashInsufficient = cashIn < totalPayableAmount;

  const isPaymentConfirmationDisabled =
    isCashInsufficient ||
    isPaymentLoading ||
    isPaymentReceiptLoading ||
    isCartEmpty ||
    isCardInfoIncomplete ||
    isMfsIncomplete ||
    isBankIncomplete;

  const isCardPayment = derivedPaymentChannel === EPaymentChannel.Card;
  const isMobilePayment = derivedPaymentChannel === EPaymentChannel.MFS;
  const isBankPayment = derivedPaymentChannel === EPaymentChannel.Bank;

  useEffect(() => {
    if (cart.length < 1) return;

    window.onbeforeunload = function (e) {
      e.preventDefault();
      return 'Changes you made may not be saved.';
    };

    return () => {
      window.onbeforeunload = null;
    };
  }, [cart.length]);

  const changeWarehouseSelectionCallback = useCallback(async (warehouseId: string) => {
    dispatch({ type: ActionKind.SetLoading, payload: true });
    dispatch({ type: ActionKind.SetCartOptions, payload: [] });
    dispatch({ type: ActionKind.EmptyCart });

    const variants = await getPOSVariantInventoriesByWarehouse({ warehouseId });

    dispatch({ type: ActionKind.SetSelectedWarehouse, payload: warehouseId });
    dispatch({
      type: ActionKind.SetCartOptions,
      payload: variants.map((variant) => ({
        ...variant,
        totalUnit: variant.quantity,
        quantity: 1,
        discount: 0,
        isDiscountInPercentage: false,
      })),
    });
    dispatch({ type: ActionKind.SetLoading, payload: false });
    resetCartOptionIndex();
  }, []);

  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        dispatch({ type: ActionKind.SetLoading, payload: true });
        const response = await getWarehousesDropdown();
        const warehouses = response.map((warehouse) => ({
          id: warehouse.id,
          name: warehouse.name,
        }));
        dispatch({ type: ActionKind.SetWarehouses, payload: warehouses });

        // Auto-select and load inventory for the first warehouse
        if (warehouses.length > 0) {
          await changeWarehouseSelectionCallback(warehouses[0].id);
        }
      } catch (error: any) {
        notification.error({
          message: 'Error Fetching Warehouse',
          description: error.message,
        });
        dispatch({ type: ActionKind.SetWarehouses, payload: [] });
      } finally {
        dispatch({ type: ActionKind.SetLoading, payload: false });
      }
    };

    fetchWarehouses();
  }, [changeWarehouseSelectionCallback]);

  useEffect(() => {
    if (!debouncedCustomerSearchValue || !debouncedCustomerSearchValue.trim()) return;

    (async () => {
      try {
        setCustomer((prev) => ({ ...prev, isSearching: true, options: [] }));
        let response = await getSearchData(SearchIndex.Client, debouncedCustomerSearchValue);
        if (debouncedCustomerSearchValue.length > 10) {
          response = response.filter((item) =>
            item.mobileNumber.includes(debouncedCustomerSearchValue),
          );
        }
        if (!response.length) {
          newCustomerNumberRef.current = debouncedCustomerSearchValue;
          const isNumeric = /^\+?\d+$/.test(debouncedCustomerSearchValue);
          if (isNumeric) {
            let number = debouncedCustomerSearchValue;
            if (number.startsWith('+880')) {
              number = number.slice(4);
              setNewCustomerCountryCode('+880');
            } else if (number.startsWith('880') && number.length > 10) {
              number = number.slice(3);
              setNewCustomerCountryCode('+880');
            } else if (number.startsWith('01') && number.length === 11) {
              number = number.slice(1);
              setNewCustomerCountryCode('+880');
            }
            setNewCustomerNumber(number);
            setNewCustomerName('');
          } else {
            setNewCustomerName(debouncedCustomerSearchValue);
            setNewCustomerNumber('');
          }
          setCustomer((prev) => ({ ...prev, isSearching: true, selected: null }));
          setIsManuallyAdding(false);
        }

        const options = response.map((item) => ({
          value: item.client_id,
          label: (
            <Typography.Text key={item.client_id}>
              {item.name} - {item.mobileNumber}
            </Typography.Text>
          ),
        }));
        setCustomer((prev) => ({ ...prev, isSearching: false, options }));
      } catch (err: any) {
        notification.error({ message: 'Failed', description: err.message });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCustomerSearchValue]);

  useEffect(() => {
    resetCartOptionIndex();
  }, [filteredCartOptions.length]);

  const resetCartOptionIndex = () => (cartOptionIndexRef.current = 0);

  const onSelectWarehouse = async (warehouseId: string | undefined) => {
    if (!warehouseId) return;

    if (cart.length === 0) {
      changeWarehouseSelectionCallback(warehouseId);
    } else if (cart.length > 0 && window.confirm('Changes you made may not be saved.')) {
      changeWarehouseSelectionCallback(warehouseId);
    } else {
      return;
    }
  };

  const getOptions = (items: IWarehouse[]) => {
    return items.map((item) => (
      <Select.Option key={item.id} value={item.id}>
        {item.name}
      </Select.Option>
    ));
  };

  const handleCustomerSelect = async (value: string) => {
    try {
      setCustomer((prev) => ({
        ...prev,
        selected: null,
        isFetching: true,
        searchValue: '',
        newCustomer: null,
      }));
      newCustomerNumberRef.current = undefined;
      setIsManuallyAdding(false);
      const client = await getClient(value);
      setCustomer((prev) => ({ ...prev, selected: client }));
    } catch (err: any) {
      notification.error({ message: 'Failed', description: 'Failed to get client.' });
    } finally {
      setCustomer((prev) => ({ ...prev, isFetching: false }));
    }
  };

  const resetPaymentInfo = () => {
    setPaymentInfo({
      cardNumber: '',
      approvalCode: '',
      transactionId: '',
      phoneNumber: '',
      bankRef: '',
    });
  };

  const handleEmptyCart = () => {
    dispatch({ type: ActionKind.EmptyCart });
    setCustomer((prev) => ({ ...prev, selected: null, newCustomer: null }));
    setDiscountInput(0);
    setCashIn(0);
    setSelectedProvider('Cash');
    resetPaymentInfo();
  };

  const handlePayment = async () => {
    let salesPaymentType: EPaymentType = EPaymentType.Cash;
    if (derivedPaymentChannel === EPaymentChannel.Card) {
      salesPaymentType = EPaymentType.Card;
    } else if (derivedPaymentChannel === EPaymentChannel.Bank) {
      salesPaymentType = EPaymentType.Cheque;
    } else if (derivedPaymentChannel === EPaymentChannel.MFS) {
      if (selectedProvider === 'bKash') salesPaymentType = EPaymentType.Bkash;
      else if (selectedProvider === 'Nagad') salesPaymentType = EPaymentType.Nagad;
      else salesPaymentType = EPaymentType.Bkash;
    }

    let paymentDetails = '';
    if (derivedPaymentChannel === EPaymentChannel.MFS) {
      paymentDetails = paymentInfo.transactionId;
    } else if (derivedPaymentChannel === EPaymentChannel.Bank) {
      paymentDetails = paymentInfo.bankRef;
    }

    let paymentMeta: Record<string, any> | undefined = undefined;
    if (derivedPaymentChannel === EPaymentChannel.Card) {
      paymentMeta = {
        cardNo: paymentInfo.cardNumber,
        approvalCode: paymentInfo.approvalCode,
      };
    } else if (derivedPaymentChannel === EPaymentChannel.MFS) {
      paymentMeta = {
        phoneNo: paymentInfo.phoneNumber || '',
        transactionId: paymentInfo.transactionId,
      };
    }

    const paymentPayload: IPayment = {
      paymentType: salesPaymentType,
      paymentChannel: derivedPaymentChannel,
      paymentProvider: selectedProvider,
      currentPaidAmount: totalPayableAmount,
      paymentDate: new Date(),
      paymentDetails,
      paymentMeta,
    };

    const saleData: CreateSaleData = {
      client: null,
      newClientDetails: null,
      salesDate: new Date(),
      salesStatus: EStatus.COMPLETED,
      warehouse: selectedWarehouse!,
      salesAddress: 'Dhaka, Bangladesh, 1000',
      vatAmount: vatRatePercent,
      // order-level discount only: the API subtracts each line's discountCost itself
      discountCost: getPosSaleDiscountCost(orderDiscountAmount),
      ...orderDiscountEntry,
      salesPayments: [paymentPayload],
      salesChannel: ESalesChannel.POS,
      invoiceProfileId,
      salesProducts: cart.map((product) => ({
        sku: product.sku,
        quantity: product.quantity,
        unitPrice: product.unitPrice,
        productVariantId: product.id,
        discountCost: getItemDiscountAmount(product),
        ...getItemDiscountEntry(product),
      })),
      transactionToken: await getTransactionToken(),
    };
    if (customer.selected !== null) {
      saleData.client = customer.selected.id;
      saleData.salesAddress = getAddressAsString(customer.selected.address) as string;
    }
    if (customer.newCustomer !== null) {
      saleData.newClientDetails = {
        name: customer.newCustomer.name,
        mobileNumber: customer.newCustomer.number,
        countryCode: customer.newCustomer.countryCode,
      };
    }

    return await createSale(saleData);
  };

  const handleConfirmPayment = async () => {
    if (!selectedWarehouse) return;

    setIsPaymentLoading(true);
    try {
      await handlePayment();
      notification.success({ message: 'Success', description: 'Successfully created a sale.' });
      handleEmptyCart();
      if (selectedWarehouse) changeWarehouseSelectionCallback(selectedWarehouse);
    } catch (err: any) {
      notification.error({ message: 'Failed', description: err.message });
    }
    setIsPaymentLoading(false);
  };

  const handlePaymentWithReceipt = async () => {
    if (!selectedWarehouse) return;

    setIsPaymentReceiptLoading(true);
    try {
      const resp = await handlePayment();

      const data = await getAllCompanyConfig();
      const configId = data[0].id;
      const companyInfo = await getCompanyInfo(configId);
      const receiptCompany = invoiceProfilesEnabled
        ? invoiceHeaderToReceiptCompany(await getInvoiceHeader({ saleId: resp.id }))
        : {
            name: companyInfo.companyName,
            number: companyInfo.companyNumber,
            address: getAddressAsString(companyInfo.address),
            tax: companyInfo.companyVatNo,
            bin: companyInfo.companyBinNo,
          };
      const date = new Date();

      Handlebars.registerHelper('incremented', function (index) {
        index++;
        return index;
      });
      Handlebars.registerPartial(
        'productTableRow',
        `<tr>
          <td>{{ product.sku }}</td>
          <td>{{ product.name }}</td>
          <td>{{ product.quantity }}</td>
          <td>{{ product.unitPrice }}</td>
          <td>{{ product.itemDiscount }}{{#if product.itemDiscountEquivalent}}<br /><small style="color:#666">{{ product.itemDiscountEquivalent }}</small>{{/if}}</td>
          <td>{{ product.netTotal }}</td>
        </tr>`,
      );

      const template = Handlebars.compile(posReceiptTemplate);
      const receipt = template({
        products: cart.map((product) => ({
          sku: product.sku,
          name: product.name,
          quantity: product.quantity,
          unitPrice: formatCurrency(product.unitPrice),
          // per-item discount as entered (10% or TK 50) and net total
          itemDiscount: formatItemDiscountEntry(product),
          itemDiscountEquivalent: describeItemDiscountEquivalent(product),
          netTotal: formatCurrency(getItemNetTotal(product)),
        })),
        company: receiptCompany,
        vat:
          vatRatePercent > 0
            ? {
                actualPrice: formatCurrency(posSaleTotals.linesSubTotal),
                percent: vatRatePercent,
                amount: formatCurrency(subtotalTaxAmount),
              }
            : null,
        netTotal: formatCurrency(totalAmount),
        total: formatCurrency(totalPayableAmount),
        perItemDiscount: formatCurrency(perItemDiscountTotal),
        orderDiscountEntry: orderDiscountEntryLabel,
        totalDiscount: formatCurrency(totalDiscountAmount),
        cash: formatCurrency(cashIn),
        change: formatCurrency(returnAmount),
        invoiceId: resp.id,
        processedBy: user?.nickname ?? null,
        date: new Intl.DateTimeFormat('en-GB').format(date),
        time: new Intl.DateTimeFormat('en-GB', { timeStyle: 'short' }).format(date),
      });
      printPosInvoice(receipt);
      notification.success({ message: 'Success', description: 'Successfully created a sale.' });
      handleEmptyCart();
      if (selectedWarehouse) changeWarehouseSelectionCallback(selectedWarehouse);
    } catch (err: any) {
      notification.error({ message: 'Failed', description: err.message });
    }
    setIsPaymentReceiptLoading(false);
  };

  const handlePrintQuotation = async () => {
    setIsPaymentLoading(true);
    try {
      const data = await getAllCompanyConfig();
      const configId = data[0].id;
      const companyInfo = await getCompanyInfo(configId);
      let quotationCompany = {
        name: companyInfo.companyName,
        email: companyInfo.companyEmail,
        number: companyInfo.companyNumber,
        website: companyInfo.companyWebsite,
        address: getAddressAsString(companyInfo.address),
        image: companyInfo.companyConfigPhoto[0]?.avatar?.url,
      };
      if (invoiceProfilesEnabled && invoiceProfileId) {
        const header = await getInvoiceHeader({ invoiceProfileId });
        quotationCompany = {
          name: header.companyName,
          email: header.companyEmail,
          number: header.companyNumber,
          website: header.companyWebsite,
          address: header.address,
          image: header.image ?? undefined,
        } as typeof quotationCompany;
      }

      Handlebars.registerHelper('incremented', function (index) {
        index++;
        return index;
      });
      Handlebars.registerPartial(
        'productTableRow',
        `<tr>
          <td>{{incremented @index}}</td>
          <td>{{ product.sku }}</td>
          <td>{{ product.name }}</td>
          <td>{{ product.quantity }}</td>
          <td>{{ product.unitPrice }}</td>
          <td>{{ product.itemDiscount }}{{#if product.itemDiscountEquivalent}}<br /><small style="color:#666">{{ product.itemDiscountEquivalent }}</small>{{/if}}</td>
          <td>{{ product.subtotal }}</td>
        </tr>`,
      );

      const template = Handlebars.compile(invoiceTemplate);
      const invoice = template({
        client: {
          name: customer.selected?.name || customer.newCustomer?.name,
          address: getAddressAsString(customer.selected?.address ?? {}),
          email: customer.selected?.email ?? '',
          mobileNumber: customer.selected?.mobileNumber || customer.newCustomer?.number,
        },
        warehouse: selectedWarehouse,
        products: cart.map((product) => ({
          sku: product.sku,
          name: product.name,
          quantity: product.quantity,
          unitPrice: formatCurrency(product.unitPrice),
          // per-item discount as entered (10% or TK 50) and discounted subtotal
          itemDiscount: formatItemDiscountEntry(product),
          itemDiscountEquivalent: describeItemDiscountEquivalent(product),
          subtotal: formatCurrency(getItemNetTotal(product)),
        })),
        subtotal: formatCurrency(subtotal),
        perItemDiscount: formatCurrency(perItemDiscountTotal),
        orderDiscountEntry: orderDiscountEntryLabel,
        totalDiscountAmount: formatCurrency(totalDiscountAmount),
        totalAmount: formatCurrency(totalPayableAmount),
        salesman: user?.nickname ?? user?.name,
        company: quotationCompany,
        note: '',
        invoiceId: getRandomString(8, 'aA#'),
        invoiceDate: new Date().toDateString(),
      });
      printPosInvoice(invoice);
      handleEmptyCart();
      notification.success({
        message: 'Success',
        description: 'Successfully created a quotation.',
      });
    } catch (err: any) {
      notification.error({ message: 'Failed', description: err.message });
    }
    setIsPaymentLoading(false);
  };

  const chosen = (
    _e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    value: { index: number; isDisabled: boolean },
  ) => {
    if (!value.isDisabled) {
      cartOptionIndexRef.current = value.index;
    }
  };

  const productOptions = filteredCartOptions.map((option, idx) => {
    const cartItem = cart.find((item) => item.id === option.id);
    const cartItemQuantity = cartItem?.quantity ?? 0;
    const isSelected = !!cartItem;
    const isOutOfStock = option.totalUnit === 0;
    const unitsLeft = option.totalUnit - cartItemQuantity - option.quantity;
    const isDisabled = option.totalUnit === 0 || unitsLeft <= -1;
    const isMaxedOut = !isOutOfStock && unitsLeft <= 0;

    return {
      label: (
        <div
          onMouseOver={(event) => {
            event.stopPropagation();
            chosen(event, { index: idx, isDisabled });
          }}
        >
          <OptionItem
            option={{ ...option, totalUnit: option.totalUnit }}
            currentWarehouseId={selectedWarehouse}
            selected={isSelected}
            disabled={isDisabled}
            outOfStock={isOutOfStock}
            maxedOut={isMaxedOut}
            increaseQuantity={() =>
              dispatch({ type: ActionKind.IncreaseCartOptionItemQuantity, payload: option.id })
            }
            decreaseQuantity={() =>
              dispatch({ type: ActionKind.DecreaseCartOptionItemQuantity, payload: option.id })
            }
          />
        </div>
      ),
      value: `${option.id}::${option.name}`,
      disabled: isDisabled,
    };
  });

  return (
    <>
      <MetaData />
      <Row gutter={[16, 0]} style={{ margin: '16px 0' }}>
        <Col lg={18} md={24}>
          {/* POS Search */}
          <Card title="">
            <div
              style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '1.5rem' }}
            >
              <Select
                showSearch
                placeholder="Select a warehouse"
                loading={isLoading}
                disabled={isLoading || !warehouses.length}
                value={selectedWarehouse}
                onSelect={(value: string) => onSelectWarehouse(value?.toString())}
                filterOption={(input, option: any) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                size="large"
                style={{ flexBasis: '20%' }}
              >
                {getOptions(warehouses)}
              </Select>

              <InvoiceProfileSelect
                value={invoiceProfileId}
                onChange={setInvoiceProfileId}
                style={{ flexBasis: '18%' }}
              />

              <AutoComplete
                size="large"
                style={{ flexGrow: '1' }}
                // options={productSearchValue === '' ? [] : productOptions}
                options={productOptions}
                placeholder="Search by Name, SKU"
                disabled={isLoading}
                value={productSearchValue}
                onChange={(value) => setProductSearchValue(value)}
                open={isDropdownVisible}
                defaultActiveFirstOption
                onDropdownVisibleChange={(flag) => setIsDropdownVisible(flag)}
                onSelect={(value: string) => {
                  const [id] = value.split('::');
                  dispatch({ type: ActionKind.AddItem, payload: id });
                  dispatch({ type: ActionKind.ResetCartOptionItemsQuantity });
                  setProductSearchValue('');
                }}
                notFoundContent={<div style={{ textAlign: 'center' }}>No data found</div>}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowUp' && isDropdownVisible) {
                    e.preventDefault();
                    const currentIndex = cartOptionIndexRef.current;
                    cartOptionIndexRef.current =
                      currentIndex === 0 ? enabledCartOptionsLength - 1 : currentIndex - 1;
                  }

                  if (e.key === 'ArrowDown' && isDropdownVisible) {
                    e.preventDefault();
                    const currentIndex = cartOptionIndexRef.current;
                    cartOptionIndexRef.current =
                      currentIndex >= enabledCartOptionsLength - 1 ? 0 : currentIndex + 1;
                  }

                  if (e.key === 'ArrowRight' && isDropdownVisible && !!filteredCartOptions.length) {
                    const option = productOptions[cartOptionIndexRef.current];
                    if (option) {
                      const id = option.value.split('::')[0];
                      dispatch({ type: ActionKind.IncreaseCartOptionItemQuantity, payload: id });
                    }
                  }

                  if (e.key === 'ArrowLeft' && isDropdownVisible && !!filteredCartOptions.length) {
                    const option = productOptions[cartOptionIndexRef.current];
                    if (option) {
                      const id = option.value.split('::')[0];
                      dispatch({ type: ActionKind.DecreaseCartOptionItemQuantity, payload: id });
                    }
                  }
                }}
              />
            </div>
          </Card>

          {/* POS Cart Table */}
          <div style={{ marginTop: '24px' }}>
            <div style={{ border: '1px solid #DEEEF8', borderRadius: '4px', overflow: 'hidden' }}>
              <Table
                dataSource={cart}
                rowKey="id"
                scroll={{ x: true }}
                pagination={false}
                loading={isLoading}
                title={() => (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', gap: '8px', verticalAlign: 'middle' }}>
                      <Icons.ShoppingOutlined size={32} style={{ fontSize: '1.5rem' }} />
                      <Typography.Title level={5}>Cart</Typography.Title>
                    </div>
                    {cart.length > 0 && (
                      <Popconfirm
                        title="Are you sure, you want to clear the cart?"
                        okText="Clear"
                        onConfirm={handleEmptyCart}
                      >
                        <Button type="link" danger>
                          Clear Cart
                          <Icons.CloseOutlined />
                        </Button>
                      </Popconfirm>
                    )}
                  </div>
                )}
                {...(cart.length >= 10 && { scroll: { x: true, y: 700 } })}
              >
                <Table.Column<ICartItem>
                  title="IMAGE"
                  dataIndex="photo"
                  align="center"
                  render={(_text, record): React.ReactNode =>
                    record.url ? (
                      <Avatar
                        size={38}
                        shape="square"
                        style={{ objectFit: 'cover' }}
                        src={record.url}
                        alt="Avatar"
                      />
                    ) : (
                      <Avatar
                        size={38}
                        shape="square"
                        style={{ objectFit: 'cover' }}
                        icon={<Icons.ShoppingCartOutlined />}
                        alt="Avatar"
                      />
                    )
                  }
                />
                <Table.Column title="SKU" dataIndex="sku" align="center" />
                <Table.Column<ICartItem>
                  title="PRODUCT NAME"
                  align="center"
                  dataIndex="name"
                  render={(name, record) => (
                    <>
                      <div>{name}</div>
                      {selectedWarehouse && (
                        <ShowroomStock
                          productVariantId={record.id}
                          currentWarehouseId={selectedWarehouse}
                        />
                      )}
                    </>
                  )}
                />
                <Table.Column
                  title="UNIT PRICE"
                  align="center"
                  dataIndex="unitPrice"
                  render={(_, record: ICartItem): React.ReactNode =>
                    formatCurrency(record.unitPrice)
                  }
                />
                <Table.Column
                  title="QUANTITY"
                  dataIndex="quantity"
                  align="center"
                  render={(_, record: ICartItem): React.ReactNode => (
                    <div
                      style={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Button
                        icon={<Icons.MinusCircleOutlined />}
                        onClick={() =>
                          dispatch({
                            type: ActionKind.DecreaseCartItemQuantity,
                            payload: record.id,
                          })
                        }
                        shape="circle"
                        style={{ border: 'none', background: 'transparent', boxShadow: 'none' }}
                      />
                      <Input
                        type="number"
                        min={1}
                        max={record.totalUnit}
                        value={record.quantity}
                        onChange={(e) => {
                          const value = e.target.value || '1';
                          let quantity = parseInt(value);
                          if (quantity > record.totalUnit) quantity = record.totalUnit;
                          dispatch({
                            type: ActionKind.ChangeQuantity,
                            payload: { id: record.id, quantity },
                          });
                        }}
                        style={{ width: '70px' }}
                      />
                      <Button
                        icon={<Icons.PlusCircleOutlined />}
                        onClick={() =>
                          dispatch({
                            type: ActionKind.IncreaseCartItemQuantity,
                            payload: record.id,
                          })
                        }
                        shape="circle"
                        style={{ border: 'none', background: 'transparent', boxShadow: 'none' }}
                      />
                    </div>
                  )}
                />

                {/* ── DISCOUNT column ─────────────────────────────────────────── */}
                <Table.Column
                  title="DISCOUNT"
                  align="center"
                  render={(_, record: ICartItem): React.ReactNode => (
                    <div>
                      <div
                        style={{
                          display: 'flex',
                          gap: '4px',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Input
                          type="number"
                          min={0}
                          // Cap percentage at 100
                          max={record.isDiscountInPercentage ? 100 : undefined}
                          value={record.discount}
                          onChange={(e) => {
                            let value = Number(e.target.value || 0);
                            if (record.isDiscountInPercentage && value > 100) value = 100;
                            dispatch({
                              type: ActionKind.ChangeItemDiscount,
                              payload: {
                                id: record.id,
                                discount: value,
                                isPercentage: record.isDiscountInPercentage,
                              },
                            });
                          }}
                          style={{ width: '70px' }}
                        />
                        <Button
                          size="small"
                          type={record.isDiscountInPercentage ? 'primary' : 'default'}
                          onClick={() =>
                            dispatch({
                              type: ActionKind.ChangeItemDiscount,
                              payload: {
                                id: record.id,
                                discount: record.discount,
                                isPercentage: true,
                              },
                            })
                          }
                        >
                          %
                        </Button>
                        <Button
                          size="small"
                          type={!record.isDiscountInPercentage ? 'primary' : 'default'}
                          onClick={() =>
                            dispatch({
                              type: ActionKind.ChangeItemDiscount,
                              payload: {
                                id: record.id,
                                discount: record.discount,
                                isPercentage: false,
                              },
                            })
                          }
                        >
                          TK
                        </Button>
                      </div>
                      {describeItemDiscountEquivalent(record) && (
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 12, whiteSpace: 'nowrap' }}
                        >
                          {describeItemDiscountEquivalent(record)}
                        </Typography.Text>
                      )}
                    </div>
                  )}
                />

                {/* ── TOTAL column — now reflects per-item discount ────────────── */}
                <Table.Column
                  title="TOTAL"
                  align="center"
                  render={(_, record: ICartItem): React.ReactNode => {
                    const lineTotal = record.unitPrice * record.quantity;
                    const itemDiscount = getItemDiscountAmount(record);
                    const netTotal = lineTotal - itemDiscount;
                    const hasDiscount = itemDiscount > 0;

                    return (
                      <div
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                      >
                        {hasDiscount && (
                          <Typography.Text delete type="secondary" style={{ fontSize: '0.75rem' }}>
                            TK {formatCurrency(lineTotal)}
                          </Typography.Text>
                        )}
                        <Typography.Text
                          strong={hasDiscount}
                          type={hasDiscount ? 'success' : undefined}
                        >
                          TK {formatCurrency(netTotal)}
                        </Typography.Text>
                        {hasDiscount && (
                          <Typography.Text type="danger" style={{ fontSize: '0.7rem' }}>
                            -{formatCurrency(itemDiscount)}
                          </Typography.Text>
                        )}
                      </div>
                    );
                  }}
                />

                <Table.Column
                  title="ACTION"
                  align="center"
                  render={(_, record: ICartItem): React.ReactNode => (
                    <Icons.DeleteOutlined
                      style={{ color: '#ef4444' }}
                      onClick={() => dispatch({ type: ActionKind.RemoveItem, payload: record.id })}
                    />
                  )}
                />
              </Table>
            </div>
          </div>
        </Col>

        {/* POS Transaction & Quotation */}
        <Col lg={6} md={24}>
          <Card
            style={{ height: '100%' }}
            title={
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <Typography.Text>
                  <Typography.Text strong style={{ fontSize: '1.1rem' }}>
                    {cardTitle}
                  </Typography.Text>
                  <Typography.Text disabled style={{ fontSize: '0.7rem' }}>
                    &nbsp; / {!isQuotation ? 'Quotation' : 'Transaction'}
                  </Typography.Text>
                </Typography.Text>
                <Icons.SwapOutlined onClick={() => setIsQuotation((toggle) => !toggle)} />
              </div>
            }
          >
            <Row gutter={8} align="middle">
              <Col span={20}>
                <Select
                  showSearch
                  loading={customer.isSearching}
                  value={customer.searchValue}
                  placeholder="Search by customer phone number"
                  defaultActiveFirstOption={false}
                  filterOption={false}
                  onSearch={(value: string) => {
                    if (value.trim()) {
                      searchTextRef.current = value;
                      setCustomer((prev) => ({ ...prev, searchValue: value, options: [] }));
                    } else {
                      setCustomer((prev) => ({ ...prev, searchValue: null, options: [] }));
                    }
                  }}
                  onSelect={handleCustomerSelect}
                  notFoundContent={null}
                  style={{ width: '100%' }}
                  options={customer.options}
                />
              </Col>
              <Col span={4}>
                <Button
                  block
                  icon={<Icons.UserAddOutlined />}
                  onClick={() => {
                    setIsManuallyAdding(true);
                    const searchValue = (searchTextRef.current || '').trim();

                    let prefilledName = '';
                    let prefilledNumber = '';
                    let prefilledCountryCode = '+880';

                    if (searchValue) {
                      const isNumeric = /^\+?\d+$/.test(searchValue);
                      if (isNumeric) {
                        let number = searchValue;
                        if (number.startsWith('+880')) {
                          number = number.slice(4);
                          prefilledCountryCode = '+880';
                        } else if (number.startsWith('880') && number.length > 10) {
                          number = number.slice(3);
                          prefilledCountryCode = '+880';
                        } else if (number.startsWith('01') && number.length === 11) {
                          number = number.slice(1);
                          prefilledCountryCode = '+880';
                        }
                        prefilledNumber = number;
                      } else {
                        prefilledName = searchValue;
                      }
                    }

                    setCustomer((prev) => ({ ...prev, selected: null, newCustomer: null }));
                    setNewCustomerName(prefilledName);
                    setNewCustomerNumber(prefilledNumber);
                    setNewCustomerCountryCode(prefilledCountryCode);
                  }}
                />
              </Col>
            </Row>

            {(isManuallyAdding ||
              (!!newCustomerNumberRef.current && !!newCustomerNumberRef.current.trim())) && (
              <Card
                style={{
                  marginTop: '0.8rem',
                  backgroundColor: '#F7F9FC',
                  borderRadius: '4px',
                }}
                bodyStyle={{ padding: '12px' }}
              >
                <Typography.Text strong style={{ fontSize: '0.9rem', color: '#595959' }}>
                  New Customer?
                </Typography.Text>
                <div
                  style={{
                    marginTop: '10px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  <Input
                    placeholder="Customer Name"
                    value={newCustomerName}
                    onChange={(e) => setNewCustomerName(e.target.value)}
                  />
                  <Input
                    placeholder="Mobile Number"
                    value={newCustomerNumber}
                    status={
                      newCustomerNumber &&
                      newCustomerCountryCode === '+880' &&
                      !/^1[3-9]\d{8}$/.test(newCustomerNumber)
                        ? 'error'
                        : ''
                    }
                    onChange={(e) => setNewCustomerNumber(e.target.value)}
                    addonBefore={
                      <Select
                        style={{ width: 80 }}
                        value={newCustomerCountryCode}
                        onChange={(val) => setNewCustomerCountryCode(val)}
                      >
                        <Select.Option value="+880">+880</Select.Option>
                      </Select>
                    }
                  />
                  <Button
                    type="primary"
                    icon={<Icons.UserAddOutlined />}
                    disabled={
                      !newCustomerName.trim() ||
                      !newCustomerNumber.trim() ||
                      (newCustomerCountryCode === '+880' &&
                        !/^1[3-9]\d{8}$/.test(newCustomerNumber))
                    }
                    onClick={() => {
                      if (newCustomerName.trim() && newCustomerNumber.trim()) {
                        setCustomer((prev) => ({
                          ...prev,
                          selected: null,
                          newCustomer: {
                            name: newCustomerName,
                            number: newCustomerNumber,
                            countryCode: newCustomerCountryCode,
                          },
                        }));
                        newCustomerNumberRef.current = undefined;
                        setIsManuallyAdding(false);
                        setNewCustomerName('');
                        setNewCustomerNumber('');
                      }
                    }}
                    style={{ width: '100%' }}
                  >
                    Add Customer
                  </Button>
                </div>
              </Card>
            )}

            {customer.isFetching && (
              <Skeleton
                paragraph={{ rows: 1 }}
                style={{
                  marginTop: '0.8rem',
                  backgroundColor: '#F7F9FC',
                  padding: '12px',
                  borderRadius: '6px',
                }}
              />
            )}
            {customer.newCustomer === null && !customer.isFetching && customer.selected && (
              <Card size="small" style={{ marginTop: '0.8rem', backgroundColor: '#F7F9FC' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <div>
                    <Typography.Text
                      type="success"
                      style={{ display: 'inline-block', marginBottom: '0.5rem' }}
                    >
                      Customer Found
                    </Typography.Text>
                    <br />
                    <Typography.Text strong style={{ fontSize: '1rem' }}>
                      {customer.selected.name}
                    </Typography.Text>
                    <br />
                    <Typography.Text type="secondary">
                      {customer.selected.mobileNumber}
                    </Typography.Text>
                  </div>
                  <Button
                    type="text"
                    size="small"
                    icon={<Icons.CloseOutlined />}
                    onClick={() =>
                      setCustomer((prev) => ({
                        ...prev,
                        selected: null,
                        searchValue: null,
                        options: [],
                      }))
                    }
                  />
                </div>
              </Card>
            )}
            {customer.selected === null && customer.newCustomer !== null && (
              <Card size="small" style={{ marginTop: '0.8rem', backgroundColor: '#F7F9FC' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <div>
                    <Typography.Text
                      type="success"
                      style={{ display: 'inline-block', marginBottom: '0.5rem' }}
                    >
                      New Customer
                    </Typography.Text>
                    <br />
                    <Typography.Text strong style={{ fontSize: '1rem' }}>
                      {customer.newCustomer.name}
                    </Typography.Text>
                    <br />
                    <Typography.Text type="secondary">
                      {customer.newCustomer.countryCode} {customer.newCustomer.number}
                    </Typography.Text>
                  </div>
                  <Button
                    type="text"
                    size="small"
                    icon={<Icons.CloseOutlined />}
                    onClick={() =>
                      setCustomer((prev) => ({
                        ...prev,
                        newCustomer: null,
                        searchValue: null,
                        options: [],
                      }))
                    }
                  />
                </div>
              </Card>
            )}

            {/* ── Order-level discount ──────────────────────────────────────── */}
            <div style={{ marginTop: '1.5rem' }}>
              <Typography.Text>Order Discount</Typography.Text>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <Input
                  type="number"
                  min={0}
                  value={discountInput === 0 ? '' : discountInput}
                  disabled={isCartEmpty}
                  onChange={(e) => {
                    const value = e.target.value || '0';
                    const parsedValue = isNaN(Number.parseFloat(value))
                      ? 0
                      : Math.abs(Number.parseFloat(value));
                    const discount =
                      isDiscountInPercentage && parsedValue > 100
                        ? 100
                        : parsedValue > subtotalAfterItemDiscounts
                          ? subtotalAfterItemDiscounts
                          : parsedValue;
                    setDiscountInput(discount);
                  }}
                />
                <div style={{ display: 'flex', gap: '0.2rem' }}>
                  <Button
                    type={isDiscountInPercentage ? 'primary' : 'default'}
                    onClick={() => {
                      setIsDiscountInPercentage(true);
                      setDiscountInput(0);
                      window.localStorage.setItem(IS_DISCOUNT_IN_PERCENTAGE, 'true');
                    }}
                  >
                    <Icons.PercentageOutlined />
                  </Button>
                  <Button
                    type={!isDiscountInPercentage ? 'primary' : 'default'}
                    onClick={() => {
                      setIsDiscountInPercentage(false);
                      setDiscountInput(0);
                      window.localStorage.setItem(IS_DISCOUNT_IN_PERCENTAGE, 'false');
                    }}
                  >
                    TK
                  </Button>
                </div>
              </div>
            </div>

            <Divider style={{ margin: '1rem 0' }} />

            {/* ── Summary ──────────────────────────────────────────────────── */}
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography.Text strong>Subtotal</Typography.Text>
                <Typography.Text>TK {formatCurrency(subtotal)}</Typography.Text>
              </div>

              {/* Show per-item discount row only when there is one */}
              {perItemDiscountTotal > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography.Text strong>Item Discounts</Typography.Text>
                  <Typography.Text type="danger">
                    - TK {formatCurrency(perItemDiscountTotal)}
                  </Typography.Text>
                </div>
              )}

              {showsVat(vatRatePercent, subtotalTaxAmount) && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography.Text strong>
                    {vatRatePercent > 0 ? `VAT (${vatRatePercent}%)` : 'Tax'}
                  </Typography.Text>
                  <Typography.Text>TK {formatCurrency(subtotalTaxAmount)}</Typography.Text>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography.Text strong>Order Discount</Typography.Text>
                <Typography.Text type="danger">
                  - TK {formatCurrency(orderDiscountAmount)}
                </Typography.Text>
              </div>

              {/* Combined discount summary */}
              {totalDiscountAmount > 0 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    backgroundColor: '#FFF1F0',
                    padding: '4px 8px',
                    borderRadius: '4px',
                  }}
                >
                  <Typography.Text strong type="danger">
                    Total Savings
                  </Typography.Text>
                  <Typography.Text strong type="danger">
                    - TK {formatCurrency(totalDiscountAmount)}
                  </Typography.Text>
                </div>
              )}
            </div>

            <Divider style={{ margin: '0.5rem 0' }} />

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography.Text strong>Total</Typography.Text>
              <Typography.Text>TK {formatCurrency(totalPayableAmount)}</Typography.Text>
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '2rem 0',
              }}
            >
              <Typography.Text>Payable Amount</Typography.Text>
              <Typography.Text strong style={{ fontSize: '1.5rem' }}>
                TK {formatCurrency(totalPayableAmount)}
              </Typography.Text>
            </div>

            {!isQuotation && (
              <>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    gap: '1rem',
                  }}
                >
                  <div style={{ flexBasis: '50%' }}>
                    <Typography.Text>Cash Paid</Typography.Text>
                    <Input
                      prefix="TK"
                      type="number"
                      min={0}
                      value={cashIn === 0 ? '' : cashIn}
                      disabled={isCartEmpty}
                      onChange={(e) => {
                        const value = e.target.value || '0';
                        const parsedValue = isNaN(Number.parseFloat(value))
                          ? 0
                          : Math.abs(Number.parseFloat(value));
                        setCashIn(parsedValue);
                      }}
                    />
                  </div>

                  <div style={{ flexBasis: '50%' }}>
                    <Typography.Text>Return</Typography.Text>
                    <p
                      style={{
                        padding: '5px 12px',
                        borderRadius: '6px',
                        backgroundColor: '#D6FFE1',
                      }}
                    >
                      TK {returnAmount <= 0 ? '' : returnAmount}
                    </p>
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <Typography.Text
                    style={{ fontWeight: 600, display: 'block', marginBottom: '0.4rem' }}
                  >
                    Payment Method / Provider
                  </Typography.Text>
                  <Select
                    showSearch
                    style={{ width: '100%' }}
                    placeholder="Search & select provider (bKash, Nagad, BRAC Bank, Card, Cash...)"
                    value={selectedProvider}
                    onChange={(val: string) => {
                      setSelectedProvider(val);
                      resetPaymentInfo();
                    }}
                    filterOption={(input, option) =>
                      ((option?.value as string) || '').toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    <Select.OptGroup label="MFS (Mobile Financial Service)">
                      {PAYMENT_PROVIDERS.filter((p) => p.channel === EPaymentChannel.MFS).map(
                        (p) => (
                          <Select.Option key={p.name} value={p.name}>
                            {p.name}
                          </Select.Option>
                        ),
                      )}
                    </Select.OptGroup>
                    <Select.OptGroup label="Bank">
                      {PAYMENT_PROVIDERS.filter((p) => p.channel === EPaymentChannel.Bank).map(
                        (p) => (
                          <Select.Option key={p.name} value={p.name}>
                            {p.name}
                          </Select.Option>
                        ),
                      )}
                    </Select.OptGroup>
                    <Select.OptGroup label="Card & Cash">
                      {PAYMENT_PROVIDERS.filter(
                        (p) =>
                          p.channel === EPaymentChannel.Card || p.channel === EPaymentChannel.Cash,
                      ).map((p) => (
                        <Select.Option key={p.name} value={p.name}>
                          {p.name}
                        </Select.Option>
                      ))}
                    </Select.OptGroup>
                  </Select>
                </div>

                {isCardPayment && (
                  <div
                    style={{
                      marginTop: '1rem',
                      display: 'flex',
                      gap: '1rem',
                      flexDirection: 'column',
                    }}
                  >
                    <div>
                      <Typography.Text>Enter Card Number (4 digit)</Typography.Text>
                      <Input
                        type="text"
                        placeholder="XXXX XXXX"
                        maxLength={4}
                        value={paymentInfo.cardNumber}
                        onChange={(e) =>
                          setPaymentInfo((prev) => ({ ...prev, cardNumber: e.target.value }))
                        }
                      />
                    </div>
                    <div>
                      <Typography.Text>Approval Code</Typography.Text>
                      <Input
                        type="text"
                        placeholder="e.g. 4567"
                        value={paymentInfo.approvalCode}
                        onChange={(e) =>
                          setPaymentInfo((prev) => ({ ...prev, approvalCode: e.target.value }))
                        }
                      />
                    </div>
                  </div>
                )}

                {isMobilePayment && (
                  <div
                    style={{
                      marginTop: '1rem',
                      display: 'flex',
                      gap: '1rem',
                      flexDirection: 'column',
                    }}
                  >
                    <div>
                      <Typography.Text>Phone Number (Optional)</Typography.Text>
                      <Input
                        type="text"
                        placeholder="e.g. 01712345678"
                        value={paymentInfo.phoneNumber}
                        onChange={(e) =>
                          setPaymentInfo((prev) => ({ ...prev, phoneNumber: e.target.value }))
                        }
                      />
                    </div>
                    <div>
                      <Typography.Text>
                        Transaction ID <span style={{ color: 'red' }}>*</span>
                      </Typography.Text>
                      <Input
                        type="text"
                        placeholder="e.g. TRX123456"
                        value={paymentInfo.transactionId}
                        onChange={(e) =>
                          setPaymentInfo((prev) => ({ ...prev, transactionId: e.target.value }))
                        }
                      />
                    </div>
                  </div>
                )}

                {isBankPayment && (
                  <div
                    style={{
                      marginTop: '1rem',
                      display: 'flex',
                      gap: '1rem',
                      flexDirection: 'column',
                    }}
                  >
                    <div>
                      <Typography.Text>
                        Bank Reference / Cheque No. <span style={{ color: 'red' }}>*</span>
                      </Typography.Text>
                      <Input
                        type="text"
                        placeholder="e.g. REF-987654"
                        value={paymentInfo.bankRef}
                        onChange={(e) =>
                          setPaymentInfo((prev) => ({ ...prev, bankRef: e.target.value }))
                        }
                      />
                    </div>
                  </div>
                )}

                <Button
                  type="primary"
                  size="large"
                  loading={isPaymentLoading}
                  disabled={isPaymentConfirmationDisabled}
                  onClick={handleConfirmPayment}
                  style={{ width: '100%', marginTop: '2rem' }}
                >
                  Confirm Payment &#8680;
                </Button>

                <Divider style={{ color: '#B4B4B4' }}>OR</Divider>

                <Button
                  type="primary"
                  size="large"
                  loading={isPaymentReceiptLoading}
                  disabled={isPaymentConfirmationDisabled}
                  onClick={handlePaymentWithReceipt}
                  style={{ width: '100%' }}
                >
                  Payment & Receipt &#8680;
                </Button>
              </>
            )}

            {isQuotation && (
              <Button
                type="primary"
                size="large"
                disabled={isPaymentLoading || !cart.length}
                onClick={handlePrintQuotation}
                style={{ width: '100%', marginTop: '1rem' }}
              >
                Print Quotation &#8680;
              </Button>
            )}

            {cashMemoPreviewEnabled && <CashMemoSampleButton />}
          </Card>
        </Col>
      </Row>
    </>
  );
};

interface OptionItemProps {
  option: ICartItem;
  currentWarehouseId?: string;
  selected: boolean;
  disabled: boolean;
  outOfStock: boolean;
  maxedOut: boolean;
  increaseQuantity: () => void;
  decreaseQuantity: () => void;
}

const OptionItem: React.FC<OptionItemProps> = ({
  option,
  currentWarehouseId,
  selected,
  disabled,
  outOfStock,
  maxedOut,
  increaseQuantity,
  decreaseQuantity,
}) => {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexBasis: '60%' }}>
        {option.url ? (
          <Badge
            size="small"
            count={selected ? <Icons.CheckCircleFilled style={{ color: '#46A5F5' }} /> : null}
            offset={[0, 5]}
          >
            <Avatar
              size={38}
              shape="square"
              style={{ objectFit: 'cover' }}
              src={option.url}
              alt="Avatar"
            />
          </Badge>
        ) : (
          <Badge
            size="small"
            count={selected ? <Icons.CheckCircleFilled style={{ color: '#46A5F5' }} /> : null}
            offset={[0, 5]}
          >
            <Avatar
              size={38}
              shape="square"
              style={{ objectFit: 'cover' }}
              icon={<Icons.ShoppingCartOutlined />}
              alt="Avatar"
            />
          </Badge>
        )}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
          }}
        >
          <Typography.Text strong disabled={disabled}>
            {option.name}
          </Typography.Text>
          <Typography.Text
            type={outOfStock || maxedOut ? 'danger' : 'secondary'}
            disabled={disabled}
          >
            {outOfStock
              ? 'Out Of Stock'
              : maxedOut
                ? 'Maxed out'
                : `Units available - ${option.totalUnit}`}
          </Typography.Text>
          {currentWarehouseId && (
            <ShowroomStock productVariantId={option.id} currentWarehouseId={currentWarehouseId} />
          )}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
          flexBasis: '10%',
        }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Icons.CaretLeftOutlined
          onClick={() => {
            if (!outOfStock) decreaseQuantity();
          }}
          style={{ fontSize: '1.5rem', cursor: outOfStock ? 'not-allowed' : 'pointer' }}
          disabled={disabled}
        />
        <Typography.Text disabled={disabled}>{option.quantity}</Typography.Text>
        <Icons.CaretRightOutlined
          onClick={() => {
            if (!outOfStock) increaseQuantity();
          }}
          style={{ fontSize: '1.5rem', cursor: outOfStock ? 'not-allowed' : 'pointer' }}
          disabled={disabled}
        />
      </div>

      <div style={{ flexBasis: '25%', textAlign: 'right' }}>
        <Typography.Text strong disabled={disabled}>
          TK {formatCurrency(option.unitPrice)}
        </Typography.Text>
      </div>
    </div>
  );
};
